<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <b-img
                  class="h-100 w-100"
                  :src="appLogoImage"
                  alt="logo"
                />
              </span>
              <h2 class="brand-text">
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <!-- Toggler Button -->

        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- Collapse trigger (right edge, desktop only) -->
    <div
      class="sidebar-collapse-trigger d-none d-xl-flex"
      :title="isVerticalMenuCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      @click="toggleCollapsed"
    >
      <feather-icon
        :icon="isVerticalMenuCollapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'"
        size="14"
      />
    </div>

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->

    <!-- Sidebar User Footer -->
    <div
      v-if="userData"
      class="sidebar-user-footer"
    >
      <div class="sidebar-user-inner">
        <!-- Avatar always visible -->
        <b-avatar
          size="34"
          :src="userData.avatar || null"
          variant="light-primary"
          class="sidebar-user-avatar flex-shrink-0"
        >
          <feather-icon
            v-if="!userData.avatar"
            icon="UserIcon"
            size="18"
          />
        </b-avatar>

        <!-- Name + actions visible when expanded -->
        <div
          v-show="!isVerticalMenuCollapsed || isMouseHovered"
          class="sidebar-user-info"
        >
          <span class="sidebar-user-name">{{ userData.name || userData.username }}</span>
        </div>
        <div
          v-show="!isVerticalMenuCollapsed || isMouseHovered"
          class="sidebar-user-actions"
        >
          <b-link
            :to="{ name: 'user-setting' }"
            class="sidebar-action-icon"
            title="Settings"
          >
            <feather-icon
              icon="SettingsIcon"
              size="16"
            />
          </b-link>
          <b-link
            class="sidebar-action-icon"
            title="Logout"
            @click="logout"
          >
            <feather-icon
              icon="LogOutIcon"
              size="16"
            />
          </b-link>
        </div>
      </div>
    </div>
    <!-- /Sidebar User Footer -->
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg, BAvatar } from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import useJwt from '@/auth/jwt/useJwt'
import { initialAbility } from '@/libs/acl/config'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    BAvatar,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem('userData') || 'null'),
    }
  },
  methods: {
    logout() {
      useJwt.logout()
        .then(() => {
          localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
          localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
          localStorage.removeItem('userData')
          this.$ability.update(initialAbility)
          this.$router.push({ name: 'auth-login' })
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Sign out failed',
              icon: 'XIcon',
              variant: 'danger',
            },
          })
        })
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => { return (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon') })

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

// Allow collapse trigger button to protrude past the sidebar edge
.main-menu {
  overflow: visible;
}

// ─── Logo ───────────────────────────────────────────────────────────────────

.main-menu .navbar-header .brand-logo img {
  max-width: 52px;
}

// ─── Sidebar User Footer ────────────────────────────────────────────────────

.main-menu-content.scroll-area {
  // Leave room for the user footer (≈ 64px)
  padding-bottom: 68px !important;
}

.sidebar-user-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.85rem 1.5rem;
  border-top: 1px solid rgba(34, 41, 47, 0.08);
  background: inherit;

  .sidebar-user-inner {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  .sidebar-user-info {
    flex: 1;
    overflow: hidden;

    .sidebar-user-name {
      display: block;
      font-size: 0.875rem;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: inherit;
    }
  }

  .sidebar-user-actions {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-shrink: 0;

    .sidebar-action-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
      border-radius: 4px;
      opacity: 0.6;
      transition: opacity 0.15s, background 0.15s;
      color: inherit;

      &:hover {
        opacity: 1;
        background: rgba(115, 103, 240, 0.08);
      }
    }
  }
}

// ─── Sidebar Collapse Trigger ───────────────────────────────────────────────

.sidebar-collapse-trigger {
  position: absolute;
  top: 54px;
  right: -13px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid rgba(34, 41, 47, 0.12);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  transition: box-shadow 0.15s, background 0.15s;

  svg {
    color: #5e5873;
    flex-shrink: 0;
  }

  &:hover {
    background: #f8f8f8;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }
}

.dark-layout .main-menu .sidebar-collapse-trigger {
  background: #283046;
  border-color: rgba(255, 255, 255, 0.1);

  svg { color: #d0d2d6; }

  &:hover { background: #343d55; }
}

// When collapsed (not hovered) — center the avatar
.main-menu:not(.expanded) .sidebar-user-footer {
  padding: 0.85rem 0;

  .sidebar-user-inner {
    justify-content: center;
  }
}

// Semi-dark / dark theme adjustments
.semi-dark-layout .main-menu .sidebar-user-footer,
.dark-layout .main-menu .sidebar-user-footer {
  border-top-color: rgba(255, 255, 255, 0.06);
}
</style>
